import React from 'react';

import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import classNames from 'classnames';
import PropTypes from 'prop-types';

import Tooltip from 'components/application/Tooltip';

import { useCurrentActor } from 'components/contexts/CurrentActor';

import CheckboxIcon from '-!svg-react-loader?name=CheckboxIcon!icons/checkbox.svg';
import DragIndicatorIcon from '-!svg-react-loader?name=DragIndicatorIcon!icons/drag-indicator.svg';

export default function SortableDocumentsRow({ isSorting, pdf }) {
  const currentActor = useCurrentActor();
  const {
    attributes: dragAttributes,
    isDragging,
    listeners: dragListeners,
    setNodeRef,
    transform: dragTransform,
    transition: dragTransition,
  } = useSortable({ id: pdf.id });

  const style = {
    transform: CSS.Transform.toString(dragTransform),
    dragTransition,
  };

  return (
    <>
      {isSorting && (
        <div
          aria-hidden='true'
          className='tw-absolute tw-h-13 tw-w-full tw-rounded-lg tw-border-1 tw-border-solid tw-border-blue-100 tw-bg-blue-025'
          role='row'
        />
      )}
      <div
        {...dragAttributes}
        className={classNames(
          'tw-group tw-relative tw-z-[1] tw-flex tw-items-center tw-border-0 tw-border-b-1 tw-border-solid tw-border-grey-100',
          isSorting ?
            isDragging ? 'tw-z-[2] tw-bg-blue-025'
            : 'tw-bg-white'
          : 'tw-bg-white hover:tw-bg-blue-025',
        )}
        ref={setNodeRef}
        role='row'
        style={style}
      >
        <div
          className='tw-flex tw-h-13 tw-w-10 tw-items-center tw-justify-center tw-border-grey-100'
          role='cell'
        >
          <div
            {...dragListeners}
            className={classNames(
              'tw-invisible tw-flex tw-h-6 tw-w-6 tw-items-center tw-justify-center tw-rounded-lg tw-border-0 tw-bg-transparent hover:tw-bg-blue-025 active:tw-bg-blue-050 group-hover:tw-visible',
              {
                'tw-cursor-grabbing': isDragging,
                'tw-cursor-grab': !isDragging,
              },
            )}
          >
            <DragIndicatorIcon
              className='[&_path]:tw-fill-grey-700'
              height={12}
              width={8}
            />
          </div>
        </div>
        <div
          className='tw-w-108 tw-border-grey-100 tw-align-middle tw-text-m tw-font-medium tw-text-grey-900'
          role='cell'
        >
          <div className='tw-flex tw-items-center'>
            <span className='tw-truncate'>{pdf.attributes.filename}</span>
          </div>
        </div>
        <div className='tw-w-30 tw-border-grey-100 tw-align-middle' role='cell'>
          <div className='flex tw-flex tw-items-center tw-justify-center'>
            {pdf.attributes.coverSheet ?
              <div className='tooltip-parent'>
                <CheckboxIcon height={24} width={24} />
                <Tooltip
                  className='tooltip-dark--max-w-xxs'
                  placement='top'
                  tooltip={`Cover sheet is always added to the beginning of the ${currentActor?.division?.attributes?.singularRAMSName || 'RAMS'}`}
                  trigger='hover'
                />
              </div>
            : ''}
          </div>
        </div>
      </div>
    </>
  );
}

SortableDocumentsRow.propTypes = {
  isSorting: PropTypes.bool.isRequired,
  pdf: PropTypes.shape({
    id: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    attributes: PropTypes.shape({
      filename: PropTypes.string.isRequired,
      coverSheet: PropTypes.bool.isRequired,
    }).isRequired,
  }).isRequired,
};
